import { Modal, Tooltip, ToolTipPosition, Tag } from "@pickit/pickit-components";
import { FlowTrasitionParamsType } from "component/flowtransition/types";
import React, { useEffect, useMemo } from "react";
import { formatDDMMYYYY, formatHHMM } from "utils/date";
import FlowTransition from "../../../component/flowtransition/FlowTransition";
import DniFinish from "./dniFinish/DniFinishContainer";
import FinishModal from "./finish/FinishModal";
import HistoryModalTransaction from "./history/HistoryModalTransaction";
import ReasonsCanceled from "./reasonsCanceled/ReasonsCanceled";
import ReasonsCanceledConfirm from "./reasonsCanceledConfirm/ReasonsCanceledConfirm";
import Undelivered from "./undelivered/Undelivered";
import { DetailTransactionPropsType } from "./types";
import "./DetailTransactions.scss";
import { useTranslation } from "react-i18next";
import { StatusTagType } from "@pickit/pickit-components/dist/ThemeProvider/types";
import { TRANSACTION_STATUS } from "constants/transactions";

const DniFinishStep = (props: FlowTrasitionParamsType) => <DniFinish {...props} />;

const getStatusTag = (tag: string, t: any): { label: string; status: Exclude<StatusTagType, null> } => {
  const variations: Record<string, { label: string; status: Exclude<StatusTagType, null> }> = {
    [TRANSACTION_STATUS.PENDING_ASSIGNMENT]: {
      label: t("tableTransactions:pendingAssignment"),
      status: "error",
    },
    [TRANSACTION_STATUS.IN_PICK_UP]: {
      label: t("tableTransactions:inPickUp"),
      status: "warning",
    },
    [TRANSACTION_STATUS.ASSIGNED]: {
      label: t("tableTransactions:assigned"),
      status: "warning",
    },
    [TRANSACTION_STATUS.IN_PICK_UP_POINT]: {
      label: t("tableTransactions:inPickUpPoint"),
      status: "warning",
    },
    [TRANSACTION_STATUS.IN_DELIVERY]: {
      label: t("tableTransactions:inDelivery"),
      status: "success",
    },
    [TRANSACTION_STATUS.IN_DELIVERY_POINT]: {
      label: t("tableTransactions:inDeliveryPoint"),
      status: "success",
    },
    [TRANSACTION_STATUS.DELIVERED]: {
      label: t("tableTransactions:delivered"),
      status: "disabled",
    },
    [TRANSACTION_STATUS.IN_RETURN_TO_SENDER]: {
      label: t("tableTransactions:inReturnToSender"),
      status: "success",
    },
    [TRANSACTION_STATUS.RETURNED_TO_SENDER]: {
      label: t("tableTransactions:returnedToSender"),
      status: "disabled",
    },
    [TRANSACTION_STATUS.LOST]: {
      label: t("tableTransactions:lost"),
      status: "disabled",
    },
    [TRANSACTION_STATUS.CANCEL]: {
      label: t("tableTransactions:cancel"),
      status: "disabled",
    },
  };
  return variations[tag] || { label: tag || "-", status: "default" };
};

export const DetailTransaction: React.FC<DetailTransactionPropsType> = ({
  detailTransaction,
  resolutionHeightModal,
  handleClose,
  isOpen,
  countryCode,
}): JSX.Element => {
  const STEP = {
    History: "History",
    DniFinish: "DniFinish",
    FinishModal: "FinishModal",
    ReasonsCanceled: "ReasonsCanceled",
    ReasonsCanceledConfirm: "ReasonsCanceledConfirm",
    Undelivered: "Undelivered",
  };
  const { t } = useTranslation();
  const testId = "modal-transaction";
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (isOpen) {
      params.set("modal", "transaction-detail");
    } else {
      params.delete("modal");
    }
    const paramsString = params.toString();
    const newUrl = paramsString
      ? `${window.location.pathname}?${paramsString}`
      : window.location.pathname;
    window.history.pushState({}, "", newUrl);
  }, [isOpen]);

  return (
    <Modal
      width="1190px"
      height={`${resolutionHeightModal}px`}
      isOpen={isOpen}
      onClose={() => {
        handleClose();
      }}
      testId={testId}
    >
      <div className="modal-transaction-container">
        <div className="container-detail-transaction-header-row modal-transaction-title">
          <div className="container-detail-transaction-header-col-sm-4">
            <h2
              className="modal-transaction-code-title"
              data-testId={`${testId}-code-title`}
            >
              {t("detailTransaction:transactionCode")}
            </h2>
            <p
              className="modal-transaction-code-subtitle"
              data-testId={`${testId}-code-value`}
            >
              {detailTransaction.transaction.transactionCode}
            </p>
          </div>

          <div className="container-detail-transaction-header-col-sm-2">
            <p
              className="modal-transaction-title order-number"
              data-testId={`${testId}-order-number-title`}
            >
              {t("detailTransaction:orderNumber")}
            </p>
            <Tooltip
              position={ToolTipPosition.bottom}
              message={detailTransaction.transaction.orderNumber}
              disabled={detailTransaction.transaction.orderNumber.length <= 18}
            >
              <p
                className="modal-transaction-order-number subtitle"
                data-testId={`${testId}-order-number-value`}
              >
                {detailTransaction.transaction.orderNumber.substring(0, 18)}
                {detailTransaction.transaction.orderNumber.length > 18 && "..."}
              </p>
            </Tooltip>
          </div>
          <div className="container-detail-transaction-header-col-sm-1" />
          <div className="container-detail-transaction-header-col-sm-2">
            <p
              className="modal-transaction-date"
              data-testId={`${testId}-date-title`}
            >
              {t("detailTransaction:date")}
            </p>
            <p
              className="modal-transaction-date subtitle"
              data-testId={`${testId}-date-value`}
            >
              <span className="table-transactions-sla-date">
                {detailTransaction.transaction.createdAt !== "" &&
                  `${formatDDMMYYYY(detailTransaction.transaction.createdAt)} - ${formatHHMM(detailTransaction.transaction.createdAt)}`}
              </span>
            </p>
          </div>
          <div className="container-detail-transaction-header-col-sm-1" />
          <div className="container-detail-transaction-header-col-sm-2 font-regular">
            <div
              className="modal-transaction-title state"
            />
            {detailTransaction.transaction.state.tag && (
              <Tag
                status={getStatusTag(detailTransaction.transaction.state.tag, t).status}
                label={getStatusTag(detailTransaction.transaction.state.tag, t).label}
                testId={`${testId}-state-value`}
              />
            )}
          </div>
        </div>
        <hr
          className="modal-transaction-separate"
          id="modal-transaction-hr-title"
        />
        <FlowTransition
          isOpen={isOpen}
          firstPage={STEP.History}
          pages={useMemo(
            () => ({
              [STEP.History]: (props: FlowTrasitionParamsType) => (
                <HistoryModalTransaction
                  cancel={STEP.ReasonsCanceled}
                  finish={STEP.FinishModal}
                  {...props}
                  countryCode={countryCode!}
                  testId={testId}
                />
              ),
              [STEP.ReasonsCanceled]: (props: FlowTrasitionParamsType) => (
                <ReasonsCanceled
                  filtersExtra={{
                    limit: 0,
                    offset: 0,
                  }}
                  reasonsCanceledConfirm={STEP.ReasonsCanceledConfirm}
                  {...props}
                />
              ),
              [STEP.ReasonsCanceledConfirm]: (props: FlowTrasitionParamsType) => (
                <ReasonsCanceledConfirm
                  filtersExtra={{
                    status: undefined,
                    name: undefined,
                    email: undefined,
                    identificationType: undefined,
                    identificationNumber: undefined,
                    vehicleType: undefined,
                    limit: undefined,
                    offset: undefined,
                  }}
                  {...props}
                />
              ),
              [STEP.DniFinish]: DniFinishStep,
              [STEP.Undelivered]: (props: FlowTrasitionParamsType) => (
                <Undelivered filtersExtra={{}} {...props} />
              ),
              [STEP.FinishModal]: (props: FlowTrasitionParamsType) => (
                <FinishModal
                  filtersExtra={{}}
                  {...props}
                  dniFinish={STEP.DniFinish}
                  undelivered={STEP.Undelivered}
                  testId={testId}
                />
              ),
            }),
            [STEP.DniFinish, STEP.FinishModal, STEP.History, STEP.ReasonsCanceled, STEP.ReasonsCanceledConfirm, STEP.Undelivered, countryCode]
          )}
        />
      </div>
    </Modal>
  );
};
